import { useEffect, useState } from "react";
import {
  ImageLibraryService,
  isLibraryFileUrl,
  ResourceObjectUrlRetireReason
} from "../../services/image-library-service";

/** `loading`: a download is in flight and nothing is painted yet. `failed`: nothing is painted and none is coming. */
export type ResourceObjectUrlStatus = "ready" | "loading" | "failed";

export interface IResourceObjectUrlState {
  /** What an `<img src>` can paint right now, or `undefined` while loading or after a failure. */
  src: string | undefined;
  status: ResourceObjectUrlStatus;
}

/** The download attempt that failed, so the failure is forgotten the moment the URL or the generation moves on. */
interface IFailedAttempt {
  contentUrl: string;
  generation: number;
}

/**
 * Turns an image library `content` URL into something an `<img src>` can paint: those
 * `resource/{id}/file` URLs need the bearer token the browser will not send for an `<img>`, so the
 * bytes come through axios and are painted as an object URL. Anything else (a `data:` URI, a
 * bundled `/images/…` path) is returned untouched. The service's cache is the single source of
 * truth — this hook paints whatever it holds and re-renders when a download lands or a write
 * retires it, so a re-upload swaps the image in place instead of blanking it. A deleted file
 * stops painting and is not asked for again.
 */
export const useResourceObjectUrl = (contentUrl: string | null | undefined): IResourceObjectUrlState => {
  const [generation, setGeneration] = useState<number>(0);
  const [, setLandings] = useState<number>(0);
  const [failedAttempt, setFailedAttempt] = useState<IFailedAttempt | undefined>(undefined);
  const [deletedUrl, setDeletedUrl] = useState<string | undefined>(undefined);
  const isLibraryFile: boolean = !!contentUrl && isLibraryFileUrl(contentUrl);
  const isDeleted: boolean = deletedUrl !== undefined && deletedUrl === contentUrl;

  useEffect((): (() => void) => {
    return ImageLibraryService.SubscribeResourceObjectUrlReset(
      (retiredFileUrls: readonly string[] | undefined, reason: ResourceObjectUrlRetireReason): void => {
        const isMine: boolean = retiredFileUrls === undefined || (!!contentUrl && retiredFileUrls.includes(contentUrl));
        if (!isMine) return;
        // Asking for a deleted file again only answers "Resource not found"; a rewritten one has new bytes waiting.
        if (reason === "deleted") setDeletedUrl(contentUrl ?? undefined);
        else setGeneration((current: number): number => current + 1);
      }
    );
  }, [contentUrl]);

  useEffect((): (() => void) | undefined => {
    if (!isLibraryFile || !contentUrl || isDeleted) return undefined;

    let isCurrent: boolean = true;
    const paintedBefore: string | undefined = ImageLibraryService.PeekResourceObjectUrl(contentUrl);
    // The cache decides what to paint; re-render only when that changed.
    const onLanded = (): void => {
      if (isCurrent && ImageLibraryService.PeekResourceObjectUrl(contentUrl) !== paintedBefore) {
        setLandings((current: number): number => current + 1);
      }
    };
    const onFailed = (): void => {
      if (isCurrent) setFailedAttempt({ contentUrl, generation });
    };
    ImageLibraryService.GetResourceObjectUrl(contentUrl).then(onLanded, onFailed);
    return (): void => {
      isCurrent = false;
    };
  }, [contentUrl, generation, isLibraryFile, isDeleted]);

  if (!contentUrl) return { src: undefined, status: "ready" };
  if (!isLibraryFile) return { src: contentUrl, status: "ready" };

  const src: string | undefined = ImageLibraryService.PeekResourceObjectUrl(contentUrl);
  if (src !== undefined) return { src, status: "ready" };
  if (isDeleted) return { src: undefined, status: "failed" };

  const isThisAttemptFailed: boolean =
    failedAttempt !== undefined && failedAttempt.contentUrl === contentUrl && failedAttempt.generation === generation;
  return { src: undefined, status: isThisAttemptFailed ? "failed" : "loading" };
};

export default useResourceObjectUrl;
